import React from 'react';
import { Phone, CheckCircle, Wrench, AlertTriangle, Gauge, Droplet, ThermometerSun } from 'lucide-react';
export function BasicMaintenancePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1606">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1607">
        <div className="container mx-auto px-4" data-id="element-1608">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1609">
            Basic Car and Truck Maintenance
          </h1>
          <p className="text-xl text-gray-100" data-id="element-1610">
            Keep Your Vehicle Running Properly and at Top Efficiency
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1611">
        <div className="max-w-4xl mx-auto" data-id="element-1612">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1613">
            <p className="text-lg text-gray-700 mb-6" data-id="element-1614">
              Garrett Auto and Truck Repair has put together a vehicle
              maintenance guide for car and truck owners. This guide is meant to
              help you keep your vehicle running properly and at top efficiency.
              It does not cover all aspects of vehicle maintenance, and it
              doesn't replace your vehicles OEM maintenance schedule. You should
              look to this guide as the most basic maintenance tips and continue
              to have your vehicle(s) routine inspections and maintenance
              performed by trained and certified mechanics.
            </p>
          </div>

          {/* Tires and Wheels Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1615">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1616">
              Garrett Auto and Truck Service's Basic Vehicle Maintenance Guide:
            </h2>

            <div className="space-y-8" data-id="element-1617">
              {/* Tire Rotation */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1618">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1619">
                  Tires and Wheels: Rotate your tires every 5000 miles
                </h3>
                <p className="text-gray-700" data-id="element-1620">
                  This is not easy if you haven't done it and don't have the
                  proper tools and equipment to do it safely. There are safety
                  concerns as any car or truck can severely injure or kill you
                  if falls on you. Bring your vehicle to Garrett Auto and Truck
                  Service if you wish to have your tires rotated for you.
                </p>
              </div>

              {/* Tire Pressure */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1621">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1622">
                  Tire Pressure Check
                </h3>
                <p className="text-gray-700" data-id="element-1623">
                  Anyone who owns a car or truck needs to own a tire pressure
                  gauge. These are very common, you can get them at Walmart, gas
                  stations and truck stops, and some grocery stores even have
                  them. If you have more than one vehicle, buy one for each car
                  and leave it in the vehicle. Use it to check each tire for
                  proper air pressure. If the air pressure is too low, you can
                  have problems and your fuel mileage will suffer. If you are
                  uncomfortable with checking your air pressure and filling it
                  to the proper PSI, you need to take it to an auto center where
                  it can be done for you. Garrett Auto performs this service.
                </p>
              </div>

              {/* Tire Tread */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1624">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1625">
                  Tire Tread Depth Check
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1626">
                  Tires need the proper amount of minimum tread to function
                  properly. If your tire tread is too worn, the tire won't
                  evacuate road debris such as water, snow, slush and sleet, and
                  may lose traction, causing loss of control. Tires where not
                  enough tread is left are commonly said to be "bald", since
                  they're so much smoother after tread wears them down.
                </p>
                <p className="text-gray-700" data-id="element-1627">
                  Most tires today have "wear bars" built in to them. If the
                  wear bars are visible, the tire tread is worn to it's limit.
                  New tires are required. If you are unfamiliar with finding and
                  recognizing tread wear bars on your tires, you can take your
                  vehicle in to an auto or truck service and have a pro check
                  them for you. If you're lucky enough to be in our area, bring
                  your car or truck to Garrett Auto and Truck Service and we'll
                  be happy to inspect your tire tread wear.
                </p>
              </div>

              {/* Clean Tires */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1628">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1629">
                  Clean Your Tires and Wheels
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1630">
                  Brake dust comes from your brake pads tightening up against
                  your brake rotor. The friction causes tiny bits and pieces of
                  the rotor and pad to flake off, forming a powder. It's nasty
                  stuff and if your leave it on the wheels too long it can get
                  really difficult to clean off. The brake dust will become
                  saturated with moisture and grime from the road, and then heat
                  from applying your brakes will bake it on to your wheels.
                </p>
                <p className="text-gray-700" data-id="element-1631">
                  Brake dust often clings to wheels because of static
                  electricity and the simple way to remove it is using damp
                  sponge with clean cold water.
                </p>
              </div>
            </div>
          </div>

          {/* Engine Maintenance Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1632">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1633">
              <Wrench className="mr-3 text-[#B1050B]" size={32} data-id="element-1634" />
              Engine Maintenance
            </h2>

            <div className="space-y-8" data-id="element-1635">
              {/* Belt Inspections */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1636">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1637">
                  Belt Inspections and Replacement
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1638">
                  Your engine is the main power supply for your vehicle. It not
                  only makes the car move forward and backward, it also powers
                  the components and accessories that make the engine itself
                  operate properly and keep the cabin (where you ride)
                  comfortable. How does it power those components and
                  accessories? The engine turns, and a rubber reinforced belt
                  transfer the rotation from the engine to the components and
                  accessories.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-1639">
                  After the engine is started, the engine powers the alternator
                  which keeps the battery charged and provides electricity to
                  everything that requires electric current from the vehicles
                  on-board computer to the lights in the cabin and the radio. In
                  short, healthy belts are mandatory for operation of the
                  vehicle.
                </p>
                <p className="text-gray-700" data-id="element-1640">
                  Make sure you have the belts inspected at least every 25,000
                  miles and we recommend replacement every 50,000 miles.
                </p>
              </div>

              {/* Oil Level Check */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1641">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1642">
                  Oil Level Check
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1643">
                  Maintain the proper oil level for optimum engine performance
                  and longevity. Most people can check the oil level in their
                  vehicle engine. Make sure the vehicle is parked on level
                  ground (not down hill or up hill, or tilted down hill
                  sideways) and make sure the engine and oil have cooled down so
                  that you don't burn yourself. Giving the vehicle some time to
                  cool down also allows oil to flow back to the oil pan, which
                  will give you a more accurate reading.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-1644">
                  Go get a clean rag. There's a dipstick under the hood, open
                  the hood of your car or truck and find the oil level dipstick
                  and pull it out. Wipe the oil off the end that was in the
                  engine the furthest and look for oil level range indication
                  markings. There is a low and high marking or there's a shaded
                  area marked on the tip of the dipstick (end that goes into the
                  oil tub or dipstick tube first).
                </p>
                <p className="text-gray-700" data-id="element-1645">
                  Slowly replace the dipstick into the tube and slide it all the
                  way down so that the end you're holding reseats, closing the
                  oil tube completely. Now slowly pull it back out and examine
                  the end with the oil level indication markings. Is the oil
                  level within the low and high markings or shaded area? If it's
                  below the low mark, you need to add a little oil.
                </p>
              </div>

              {/* Engine Oil Replacement */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1646">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1647">
                  Engine Oil Replacement (Time for an oil change)
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1648">
                  Manufacturers recommend different intervals for oil changes.
                  Generally, and oil change every 3,000 miles is safe. The best
                  service recommendation is the one your vehicle manufacturer
                  recommends for your vehicle and engine. When you have your oil
                  changed make sure your oil filter is changed as well. This is
                  also a good time to have the air filter changed.
                </p>
                <p className="text-gray-700" data-id="element-1649">
                  Clean filters allow the vehicle engine to run more efficiently
                  and last longer. Old dirty filters become clogged with debris
                  and road grime, causing flow restrictions that may impede
                  proper operation of the engine. Worse, dirty filters can allow
                  that debris to find it's way past the filters and into your
                  engine and components, which can cause expensive damage to
                  your engine and fuel system.
                </p>
              </div>

              {/* Engine Coolant */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1650">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1651">
                  Engine Coolant Level Check
                </h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" data-id="element-1652">
                  <p className="text-gray-800" data-id="element-1653">
                    <strong data-id="element-1654">NOTE:</strong> If you plan on checking your coolant
                    levels, make certain the vehicle engine and coolant have had
                    enough time to cool down. DO NOT REMOVE Radiator cap from
                    the radiator when the engine and coolant are still hot from
                    the engine running.
                  </p>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1655">
                  Coolant provides essential functions for your vehicle engine.
                  It keeps the engine from over-heating. Coolant also must not
                  freeze during the winter months, so make sure the coolant
                  mixture is appropriate for your climate. Coolant usually comes
                  in concentrate form and you mix it with water. Make sure to
                  read your vehicle manufacturer's recommendations for coolant
                  and follow these recommendations.
                </p>
                <p className="text-gray-700" data-id="element-1656">
                  Your manufacturer guide will explain where to find your
                  coolant reservoir as well as provide basic instructions for
                  check coolant level and maintaining it. If you don't want to
                  deal with hot coolant or have no inclination to perform
                  vehicle maintenance, take your vehicle to a trusted, certified
                  auto mechanic in your area and have the coolant levels checked
                  and filled by professionals.
                </p>
              </div>

              {/* Dash Lights */}
              <div className="border-l-4 border-[#B1050B] pl-6" data-id="element-1657">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1658">
                  Dash Lights/Warning Lights
                </h3>
                <p className="text-gray-700" data-id="element-1659">
                  If you see any warning lights indicating there may be a
                  problem with your vehicle, we recommend you bring it in for
                  diagnosis. Our certified professional mechanics will diagnose
                  any problems and help you get back on the road. If you're not
                  lucky enough to be in our area, (Farmington Hills, MI,
                  bordering Novi and easy to get to from Northville and the
                  Detroit Metropolitan area), then we highly recommend you find
                  trusted certified professional mechanics in your area.
                </p>
              </div>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1660">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1661">
              Garrett Auto and Truck Repair Mission Statement
            </h2>
            <p className="text-gray-700" data-id="element-1662">
              Garrett Auto & Truck Repair Service is here to serve you by
              performing the highest quality automotive & truck repairs at fair
              prices, using only proven materials of the highest quality. Our
              service is backed by our personal pledge of customer satisfaction.
              We will provide you, our customer, with outstanding professional
              courtesy and service. And we will stand behind our workmanship.
            </p>
          </div>

          {/* Auto Repair Services */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1663">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1664">
              Auto Repair, Truck Repair and Maintenance Services
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-1665">
              Garrett Auto & Truck Repair Service believes in preventative
              maintenance. Bring your vehicle in for routine oil checks and get
              your oil changed. New, clean oil will lubricate the engine
              appropriately and help protect it from unnecessary wear and tear.
              Oil filter replacement, air filter replacement, and other routine
              maintenance services will keep your vehicle running longer and
              better.
            </p>
            <p className="text-gray-700" data-id="element-1666">
              We'll help you save time and money by providing the diagnostic,
              repair and replacement services recommended by your vehicle
              manufacturer. We've put together a great guide for basic
              automotive and truck maintenance that you can refer to if you wish
              to keep your vehicle running right. These are things you can
              easily do if you have the time and want to put in the effort.
            </p>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1667">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1668">
              Need Professional Maintenance?
            </h2>
            <p className="text-xl mb-6" data-id="element-1669">
              Contact Garrett Auto & Truck Service today for expert maintenance
              services
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1670">
              <Phone className="mr-3" size={24} data-id="element-1671" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1672">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}